import React from "react";
import { useTemplateListView } from "../TemplateList.context";
import {
  TemplateFilter,
  TemplateTag,
  TemplateType,
} from "../TemplateList.types";
import { sectionPropsByFilter } from "./DragAndDropTab.helpers";
import { DragAndDropTabProps } from "./DragAndDropTab.types";
import { useConfigView } from "../../../Config.context";
import { useDeleteActivityMutation } from "../../../app/useGeneratedApi";
import { useTemplateSectionQuery } from "./useTemplateSectionQuery";
import useInfiniteTemplatesQuery from "../useInfiniteTemplatesQuery";

export function useDragAndDropTab(props: DragAndDropTabProps) {
  const { refetchSections, emptyStateMessage, showSelected } = props;

  const { selectedTemplate, selectable } = useTemplateListView();

  const [searchKeyword, setSearchKeyword] = React.useState("");

  const [activeFilter, setActiveFilter] = React.useState<TemplateFilter>("All");

  const [activeTitle, setActiveTitle] = React.useState<string>();

  const [activeType, setActiveType] = React.useState<TemplateType>();

  const [activeSaved, setActiveSaved] = React.useState<boolean>();

  const [isFavoritesSelected, setIsFavoritesSelected] = React.useState(false);

  const [activeRenderWithEjs, setActiveRenderWithEjs] =
    React.useState<boolean>();

  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);

  const [selectedTags, setSelectedTags] = React.useState<TemplateTag[]>([]);

  const tagIds = selectedTags.map((tag) => tag.id!);

  const shouldShowFilteredSection = activeFilter !== "All";

  const {
    savedTemplates,
    staffPicksTemplates,
    automationTemplates,
    colorfulTemplates,
    professionalTemplates,
    minimalistTemplates,
    shouldShowSavedAndStaffPicks,
    shouldShowSaved,
    shouldShowAutomation,
    shouldShowColorful,
    shouldShowProfessional,
    shouldShowMinimalist,
    totalSaved,
    totalStaffPicks,
    totalAutomation,
    totalColorful,
    totalProfessional,
    totalMinimalist,
    isEmpty: isTemplatesEmpty,
    isLastSaved,
    isLastStaffPicks,
    isLastAutomation,
    isLastColorful,
    isLastProfessional,
    isLastMinimalist,
    isLoadingSaved,
    isLoadingStaffPicks,
    isLoadingAutomation,
    isLoadingColorful,
    isLoadingProfessional,
    isLoadingMinimalist,
    refetchTemplates,
    removeTemplates,
  } = useTemplateSectionQuery({
    searchKeyword,
    tagIds,
    favorited: isFavoritesSelected || undefined,
    enabled: !shouldShowFilteredSection,
    refetchSections,
  });

  const {
    templates: filteredTemplates,
    fetchMore,
    hasMore,
    total: totalFilteredTemplates,
    refetch: refetchFilterdSections,
    isEmpty: isFilteredTemplatesEmpty,
    loading: isLoading,
  } = useInfiniteTemplatesQuery(
    activeSaved,
    activeType,
    searchKeyword,
    selectedTags,
    isFavoritesSelected || undefined,
    "DRAG_AND_DROP",
    shouldShowFilteredSection
  );

  const refreshPage = shouldShowFilteredSection
    ? refetchFilterdSections
    : refetchTemplates;

  const { mutateAsync: deleteActivityMutateAsync } =
    useDeleteActivityMutation();

  const search = React.useCallback((value: string) => {
    setSearchKeyword(value);
  }, []);

  const deleteSelected = React.useCallback(async () => {
    await deleteActivityMutateAsync({ data: { ids: selectedIds } });
    setSelectedIds([]);
    refreshPage();
  }, [deleteActivityMutateAsync, refreshPage, selectedIds]);

  const deselectAll = React.useCallback(() => {
    setSelectedIds([]);
  }, []);

  const updateSelectedTags = React.useCallback((tags?: TemplateTag[]) => {
    setSelectedTags(tags || []);
  }, []);

  const deselectTag = React.useCallback((tagId: string) => {
    setSelectedTags((prev) => {
      return prev.filter((tag) => tag.id !== tagId);
    });
  }, []);

  const toggleSelect = React.useCallback((id?: string) => {
    if (!id) {
      return;
    }
    setSelectedIds((prev) => {
      const index = prev.findIndex((i) => i === id);
      if (index === -1) {
        return [...prev, id];
      } else {
        const next = [...prev];
        next.splice(index, 1);
        return next;
      }
    });
  }, []);

  const isSelected = React.useCallback(
    (id?: string) => {
      if (!id) {
        return false;
      }
      return selectedIds.includes(id);
    },
    [selectedIds]
  );

  const changeActiveFilter = React.useCallback(
    (value: TemplateFilter) => {
      const { title, saved, type, renderWithEjs } = sectionPropsByFilter(value);
      setActiveTitle(title);
      setActiveSaved(saved);
      setActiveType(type);
      setActiveFilter(value);
      setActiveRenderWithEjs(renderWithEjs);
      removeTemplates();
    },
    [removeTemplates]
  );

  const toggleAll = React.useCallback(
    (value: boolean) => {
      if (value) {
        const ids = shouldShowFilteredSection
          ? filteredTemplates.map((template: any) => template.id!)
          : savedTemplates.map((template: any) => template.id!);
        setSelectedIds(ids);
      } else {
        setSelectedIds([]);
      }
    },
    [filteredTemplates, savedTemplates, shouldShowFilteredSection]
  );

  const showAll = React.useCallback(
    (saved?: boolean, type?: TemplateType) => {
      if (saved) {
        changeActiveFilter("Saved");
      } else if (type === "STAFF_PICKED") {
        changeActiveFilter("StaffPicks");
      } else if (type === "AUTOMATION") {
        changeActiveFilter("Automation");
      } else if (type === "COLORFUL") {
        changeActiveFilter("Colorful");
      } else if (type === "PROFESSIONAL") {
        changeActiveFilter("Professional");
      } else if (type === "MINIMALIST") {
        changeActiveFilter("Minimalist");
      }
    },
    [changeActiveFilter]
  );

  const allSelected = React.useMemo(() => {
    if (shouldShowFilteredSection) {
      return (
        activeFilter === "Saved" &&
        filteredTemplates.length > 0 &&
        filteredTemplates.length === selectedIds.length
      );
    } else {
      return (
        savedTemplates.length === selectedIds.length &&
        savedTemplates.length > 0
      );
    }
  }, [
    activeFilter,
    filteredTemplates.length,
    savedTemplates.length,
    selectedIds.length,
    shouldShowFilteredSection,
  ]);

  const allSelectedDisabled = React.useMemo(() => {
    if (shouldShowFilteredSection) {
      return activeFilter !== "Saved" || filteredTemplates.length === 0;
    } else {
      return savedTemplates.length === 0;
    }
  }, [
    activeFilter,
    filteredTemplates.length,
    savedTemplates.length,
    shouldShowFilteredSection,
  ]);

  const isEmpty = shouldShowFilteredSection
    ? isFilteredTemplatesEmpty
    : isTemplatesEmpty;

  return {
    allSelectedDisabled,
    toggleAll,
    selectedIds,
    allSelected,
    activeFilter,
    automationTemplates,
    colorfulTemplates,
    professionalTemplates,
    minimalistTemplates,
    changeActiveFilter,
    deleteSelected,
    deselectAll,
    deselectTag,
    isLoading,
    isLoadingAutomation,
    isLoadingColorful,
    isLoadingProfessional,
    isLoadingMinimalist,
    isLoadingSaved,
    isLoadingStaffPicks,
    isSelected,
    refetchSections,
    savedTemplates,
    search,
    selectedTags,
    shouldShowAutomation,
    shouldShowColorful,
    shouldShowProfessional,
    shouldShowMinimalist,
    shouldShowSaved,
    shouldShowSavedAndStaffPicks,
    showSelected,
    staffPicksTemplates,
    toggleSelect,
    updateSelectedTags,
    totalSaved,
    totalStaffPicks,
    totalAutomation,
    totalColorful,
    totalProfessional,
    totalMinimalist,
    isEmpty,
    emptyStateMessage,
    isLastSaved,
    isLastStaffPicks,
    isLastAutomation,
    isLastColorful,
    isLastProfessional,
    isLastMinimalist,
    searchKeyword,
    shouldShowFilteredSection,
    filteredTemplates,
    fetchMore,
    hasMore,
    totalFilteredTemplates,
    activeTitle,
    activeSaved,
    activeType,
    activeRenderWithEjs,
    showAll,
    selectedTemplate,
    selectable,
    isFavoritesSelected,
    setIsFavoritesSelected,
  };
}
